import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Scan, ProductionBatch } from '../../../model';
import { ModelState } from '../../../store/model/model.state';

@Component({
  selector: 'app-batch-scan-details',
  imports: [CommonModule],
  template: `
    <div class="scan-details">
      <h3>Scan Details</h3>
      @if (scan(); as s) {
        <div class="detail-row"><strong>Sequence:</strong> {{ s.sequenceInBatch }}</div>
        <div class="detail-row"
          ><strong>Status:</strong> <span [class]="'status-' + s.state.toLowerCase()">{{ s.state }}</span></div
        >
        <div class="detail-row"><strong>Time:</strong> {{ s.scannedTime | date: 'mediumTime' }}</div>

        <div class="section-title">Measured</div>
        <div class="detail-row"><strong>L*:</strong> {{ s.median?.l | number: '1.2-2' }}</div>
        <div class="detail-row"><strong>a*:</strong> {{ s.median?.a | number: '1.2-2' }}</div>
        <div class="detail-row"><strong>b*:</strong> {{ s.median?.b | number: '1.2-2' }}</div>

        @if (s.predicted) {
          <div class="section-title">Predicted</div>
          <div class="detail-row"><strong>L*:</strong> {{ s.predicted.l | number: '1.2-2' }}</div>
          <div class="detail-row"><strong>a*:</strong> {{ s.predicted.a | number: '1.2-2' }}</div>
          <div class="detail-row"><strong>b*:</strong> {{ s.predicted.b | number: '1.2-2' }}</div>
        }

        <div class="section-title">Batch</div>
        <div class="detail-row"><strong>PO:</strong> {{ batch()?.poName ?? '—' }}</div>

        <div class="section-title">Model</div>
        <div class="detail-row"><strong>ID:</strong> <span class="mono">{{ s.inspectionModelId ?? '—' }}</span></div>
        @if (inspectionModel(); as m) {
          <div class="detail-row"><strong>Version:</strong> {{ m.version }}</div>
          @if (m.description) {
            <div class="detail-row detail-row--wrap"><strong>Description:</strong> {{ m.description }}</div>
          }
        }
      } @else {
        <p class="placeholder">Select a point on the chart to view details.</p>
      }
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        height: 100%;
        overflow-y: auto;
        border-left: 1px solid #eee;
        padding: 1rem;
        background: #f9f9f9;
      }
      .scan-details {
        font-size: 0.9rem;
      }
      h3 {
        margin-top: 0;
        border-bottom: 2px solid #ddd;
        padding-bottom: 0.5rem;
      }
      .detail-row {
        margin-bottom: 0.5rem;
        display: flex;
        justify-content: space-between;
        gap: 0.5rem;
      }
      .detail-row--wrap {
        flex-wrap: wrap;
      }
      .section-title {
        margin-top: 1rem;
        margin-bottom: 0.5rem;
        font-weight: bold;
        border-bottom: 1px solid #eee;
        color: #666;
      }
      .placeholder {
        font-style: italic;
        color: #888;
        text-align: center;
        margin-top: 2rem;
      }
      .mono {
        font-family: monospace;
        font-size: 0.8em;
        word-break: break-all;
      }
      .status-passed {
        color: green;
        font-weight: bold;
      }
      .status-failed {
        color: red;
        font-weight: bold;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BatchScanDetailsComponent {
  scan = input<Scan | null>(null);

  /**
   * The batch the scan belongs to — passed separately because the scan's
   * productionBatch back-reference is not populated when scans arrive
   * as children of a batch API response (avoids circular serialisation).
   */
  batch = input<ProductionBatch | null>(null);

  private modelState = inject(ModelState);

  /** Resolved from the scan's inspectionModelId against the global model registry. */
  inspectionModel = computed(() => {
    const id = this.scan()?.inspectionModelId;
    if (!id) return null;
    const result = this.modelState.all.find(m => m.id === id) ?? null;
    console.log('[batch-scan-details] inspectionModel resolved:', result);
    return result;
  });
}
